'use client';

import { useEffect } from 'react';
import { UseFormReturn, FieldValues } from 'react-hook-form';

import { logger } from '../utils';

export interface UseFormPersistOptions {
  /**
   * Storage key for persisting form data
   */
  storageKey: string;

  /**
   * Storage type
   * @default 'localStorage'
   */
  storage?: 'localStorage' | 'sessionStorage';

  /**
   * Fields to exclude from persistence
   */
  exclude?: string[];

  /**
   * Whether to clear storage on successful submit
   * @default true
   */
  clearOnSubmit?: boolean;

  /**
   * Debounce delay in milliseconds
   * @default 500
   */
  debounceMs?: number;
}

/**
 * Hook to persist form data to local/session storage
 * Automatically saves form state and restores on mount
 */
export function useFormPersist<TFieldValues extends FieldValues = FieldValues>(
  form: UseFormReturn<TFieldValues>,
  options: UseFormPersistOptions
) {
  const {
    storageKey,
    storage = 'localStorage',
    exclude = [],
    clearOnSubmit = true,
    debounceMs = 500,
  } = options;

  const storageApi = typeof window !== 'undefined' ? window[storage] : null;

  // Restore form data on mount
  useEffect(() => {
    if (!storageApi) return;

    try {
      const savedData = storageApi.getItem(storageKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);

        // Filter out excluded fields
        const filteredData = Object.keys(parsedData).reduce((acc, key) => {
          if (!exclude.includes(key)) {
            acc[key as keyof TFieldValues] = parsedData[key];
          }
          return acc;
        }, {} as Partial<TFieldValues>);

        // Reset form with saved data
        form.reset(filteredData as TFieldValues);
      }
    } catch (error) {
      logger.error('Failed to restore form data:', error);
    }
  }, [storageKey, storage, exclude, form, storageApi]);

  // Save form data on change with debounce
  useEffect(() => {
    if (!storageApi) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const subscription = form.watch((data) => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        try {
          // Filter out excluded fields
          const filteredData = Object.keys(data).reduce(
            (acc, key) => {
              if (!exclude.includes(key) && (data as Record<string, unknown>)[key] !== undefined) {
                acc[key] = (data as Record<string, unknown>)[key];
              }
              return acc;
            },
            {} as Record<string, unknown>
          );

          storageApi.setItem(storageKey, JSON.stringify(filteredData));
        } catch (error) {
          logger.error('Failed to save form data:', error);
        }
      }, debounceMs);
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [form, storageKey, storage, exclude, debounceMs, storageApi]);

  // Clear storage on successful submit
  useEffect(() => {
    if (!clearOnSubmit || !storageApi) return;

    const handleSubmitSuccess = () => {
      try {
        storageApi.removeItem(storageKey);
      } catch (error) {
        logger.error('Failed to clear form data:', error);
      }
    };

    // Listen for form submission state
    const subscription = form.watch((_, { type }) => {
      if (type === 'change' && form.formState.isSubmitSuccessful) {
        handleSubmitSuccess();
      }
    });

    return () => subscription.unsubscribe();
  }, [form, storageKey, storage, clearOnSubmit, storageApi]);

  // Manual clear function
  const clearStorage = () => {
    if (storageApi) {
      try {
        storageApi.removeItem(storageKey);
      } catch (error) {
        logger.error('Failed to clear form data:', error);
      }
    }
  };

  return { clearStorage };
}
